import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import './Auth.css';
import Footer from '../../Footer';
import { ArrowRightIcon, CheckIcon, TeamIcon, UserIcon } from '../../public/icons';
import {
  formatSubscriptionPrice,
  getPublishedSubscriptionPlan,
} from '../../../constants/subscriptionPlans';
import {
  getBusinessInviteTokenFromNext,
  getSignupMode,
  isSignupAccountMode,
  storeSignupMode,
} from '../../../utils/signupMode';

const formatMonthly = (accountMode) =>
  formatSubscriptionPrice(getPublishedSubscriptionPlan(accountMode).price, 'en-US');

const MODES = [
  {
    value: 'single',
    title: 'Just me',
    eyebrow: `Single mode · ${formatMonthly('single')}/mo`,
    description: 'One person, one calendar, one booking page.',
    features: [
      'Your own services, hours and page',
      'Client records, payments, reminders',
      'No team admin to wade through',
    ],
    icon: UserIcon,
    tone: 'clay',
  },
  {
    value: 'team',
    title: 'With a team',
    eyebrow: `Team mode · ${formatMonthly('team')}/mo`,
    description: 'One workspace for owners, admins and providers.',
    features: [
      'Everything in Single Mode, plus team management',
      'Shared services with provider assignments',
      'Individual calendars, one team view',
    ],
    icon: TeamIcon,
    tone: 'sage',
  },
];

const AccountModeSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search || '');
  const nextPath = params.get('next') || '';
  const businessInviteToken = getBusinessInviteTokenFromNext(nextPath);
  const requestedMode = params.get('mode');
  const hasRequestedMode = isSignupAccountMode(requestedMode);
  // ?invite= is how admin recruiter invites arrive (emailed link and the admin
  // "Copy link"). A recruiter has no workspace mode, so those skip the chooser.
  const inviteCode = (params.get('invite') || '').trim();
  const checkingInvite = Boolean(inviteCode) && !hasRequestedMode && !businessInviteToken;
  const [inviteCheck, setInviteCheck] = useState(checkingInvite ? 'pending' : 'none');
  const [selectedMode, setSelectedMode] = useState(getSignupMode(location.search));
  const [switchingAvailable, setSwitchingAvailable] = useState(false);
  const cardRefs = useRef([]);

  useEffect(() => {
    if (businessInviteToken) {
      navigate(`/signup/details${location.search}`, { replace: true });
      return;
    }
    if (hasRequestedMode) {
      storeSignupMode(requestedMode);
      navigate(`/signup/details${location.search}`, { replace: true });
    }
  }, [businessInviteToken, hasRequestedMode, location.search, navigate, requestedMode]);

  useEffect(() => {
    if (!checkingInvite) return undefined;
    let active = true;
    setInviteCheck('pending');
    axios.post('/api/auth/signup/validate-referral', { code: inviteCode })
      .then((response) => {
        if (!active) return;
        // Keyed on the role, not validity: an email-pinned invite reports
        // invalid until the email is typed, and it is still a recruiter invite.
        if (response?.data?.signupRole === 'recruiter') {
          // The details form bounces back here when no mode is stored, so the
          // skip has to leave one behind exactly as Continue does. Single is a
          // placeholder: a recruiter account has no workspace mode and the
          // server ignores it for that role.
          const params = new URLSearchParams(location.search || '');
          if (!storeSignupMode('single')) params.set('mode', 'single');
          navigate(`/signup/details?${params.toString()}`, { replace: true });
        } else {
          setInviteCheck('done');
        }
      })
      .catch(() => {
        // Best effort: a failed lookup just shows the chooser.
        if (active) setInviteCheck('done');
      });
    return () => { active = false; };
  }, [checkingInvite, inviteCode, location.search, navigate]);

  useEffect(() => {
    let active = true;
    axios.get('/api/features/account-mode').then((response) => {
      if (active) setSwitchingAvailable(Boolean(response.data?.modeSwitchingAvailable));
    }).catch(() => {
      if (active) setSwitchingAvailable(false);
    });
    return () => { active = false; };
  }, []);

  const selectMode = (mode, focus = false) => {
    setSelectedMode(mode);
    storeSignupMode(mode);
    if (focus) {
      const index = MODES.findIndex((candidate) => candidate.value === mode);
      cardRefs.current[index]?.focus();
    }
  };

  const handleKeyDown = (event, index) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    const delta = ['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1;
    const nextIndex = (index + delta + MODES.length) % MODES.length;
    selectMode(MODES[nextIndex].value, true);
  };

  const continueSignup = () => {
    if (!selectedMode) return;
    // When storage is blocked the write is a no-op, so the URL becomes the only
    // way the mode survives the hop to /signup/details.
    const params = new URLSearchParams(location.search || '');
    if (!storeSignupMode(selectedMode)) params.set('mode', selectedMode);
    const search = params.toString();
    navigate(`/signup/details${search ? `?${search}` : ''}`);
  };

  // Hidden only while the current invite is being looked up, so a query-string
  // change mid-lookup can never leave the chooser blank.
  if (businessInviteToken || hasRequestedMode || (checkingInvite && inviteCheck === 'pending')) return null;

  return (
    <>
      <div className="auth-page auth-page--signup auth-page--mode-selection">
        <span className="deco deco--1 auth-deco--tr" aria-hidden="true" />
        <div className="auth-inner auth-inner--mode-selection">
          <header className="auth-header">
            <span className="eyebrow">Create your account · Step 1 of 3</span>
            <h1>How will you use Haastia?</h1>
            <p className="sub">Pick whichever fits how you work today.</p>
            {switchingAvailable && <p className="mode-switch-note">You can switch modes later.</p>}
          </header>

          <div className="mode-card-grid" role="radiogroup" aria-label="Account mode">
            {MODES.map((mode, index) => {
              const selected = selectedMode === mode.value;
              const Icon = mode.icon;
              const sage = mode.tone === 'sage';
              return (
                <button
                  key={mode.value}
                  ref={(node) => { cardRefs.current[index] = node; }}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`account-mode-card${selected ? ' account-mode-card--selected' : ''}`}
                  onClick={() => selectMode(mode.value)}
                  onKeyDown={(event) => handleKeyDown(event, index)}
                  tabIndex={selected || (!selectedMode && index === 0) ? 0 : -1}
                >
                  <span className="account-mode-card__top">
                    <span
                      className={`account-mode-card__icon${sage ? ' account-mode-card__icon--sage' : ''}`}
                    >
                      <Icon size={23} />
                    </span>
                    <span className="account-mode-card__indicator" aria-hidden="true" />
                  </span>
                  <span className="account-mode-card__copy">
                    <span
                      className={`account-mode-card__eyebrow${sage ? ' account-mode-card__eyebrow--sage' : ''}`}
                    >
                      {mode.eyebrow}
                    </span>
                    <strong>{mode.title}</strong>
                    <span className="account-mode-card__description">{mode.description}</span>
                    <span className="account-mode-card__features" role="list">
                      {mode.features.map((feature) => (
                        <span role="listitem" key={feature}>
                          <CheckIcon size={16} />
                          {feature}
                        </span>
                      ))}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mode-selection-bar">
            <p>Free trial first — you won&apos;t be charged until it ends.</p>
            <div className="mode-selection-bar__actions">
              <Link className="btn btn-secondary btn-md" to="/login">
                I already have an account
              </Link>
              <button
                type="button"
                className="btn btn-primary btn-md"
                onClick={continueSignup}
                disabled={!selectedMode}
              >
                Continue
                <ArrowRightIcon size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AccountModeSelection;
